import React from 'react'
import PropTypes from 'prop-types'
import { Box, ButtonBase, Stack, Typography } from '@mui/material'
import { useSmartTranslation } from '../../../../../../../generic/hooks/useSmartTranslation'
import { useDateFormatter } from '../../../../../../../generic/hooks/useDateFormatter'
import { priorityLabelKey } from '../../../../../qualityIncidences/qualityIncidencePriority'
import { EMPTY_VALUE } from '../../../utils/jobDetailFormat'
import { BlockState, DetailSection, HeaderCell, Pill } from '../JobDetailPrimitives'
import { SystemValueChip } from '../../../../../../../generic/systemValues/SystemValueChip'

// Historia 160 (CU7, RN33-RN35, D21, G5, mock 5) — INCIDENCIAS ABIERTAS y ACCIONES DE <código>.
//
// Solo lectura (RN35): ni un control de gestión. Las acciones de TODAS las incidencias viajan en D4,
// así que elegir otra incidencia no llama al servidor (S77). Prioridad y estado se rotulan con las
// MISMAS claves y la misma fuente que el widget de calidad (T12): nada nuevo. El código se pinta tal
// como llega ("142", no "QI-0142"). La celda de pendientes usa su clave propia (G5).

const PRIORITY_TONE = { LOW: 'default', MEDIUM: 'warning', HIGH: 'error', CRITICAL: 'error' }
const ACTION_TONE = { PENDING: 'error', MADE: 'success', PARTIAL_MADE: 'warning' }

const INCIDENCE_GRID = 'minmax(0, 0.8fr) minmax(0, 2.6fr) minmax(0, 0.8fr) minmax(0, 0.9fr) minmax(0, 1.2fr)'
const ACTION_GRID = 'minmax(0, 2.6fr) minmax(0, 1.2fr) minmax(0, 0.8fr) minmax(0, 0.9fr)'

const IncidencesTab = ({ block = {}, selectedUuid = '', onSelect = () => {}, onRetry = () => {} }) => {
  const { t_ } = useSmartTranslation()
  const { formatDate } = useDateFormatter()
  const incidences = block.data?.incidences || []
  const selected = incidences.find((incidence) => incidence.uuid === selectedUuid)

  // RN34: fecha de ejecución si está hecha; de vencimiento si no.
  const actionDate = (action) => {
    const instant = action.status === 'MADE' ? action.executionDate : action.expirationDate
    return instant ? formatDate(instant) : EMPTY_VALUE
  }

  return (
    <BlockState status={block.status} onRetry={onRetry}>
      <Stack spacing={2}>
        <DetailSection title={t_('Open incidences')} testId='incidences-block'>
          <Box role='table' aria-label={t_('Open incidences')}>
            <Box role='row' sx={{ display: 'grid', gridTemplateColumns: INCIDENCE_GRID, gap: 2, px: 2, py: 1, borderBottom: 1, borderColor: 'divider' }}>
              <HeaderCell>{t_('Code')}</HeaderCell>
              <HeaderCell>{t_('Incidence')}</HeaderCell>
              <HeaderCell>{t_('Priority')}</HeaderCell>
              <HeaderCell>{t_('Status')}</HeaderCell>
              <HeaderCell>{t_('Pending actions')}</HeaderCell>
            </Box>
            {incidences.map((incidence) => {
              const isSelected = incidence.uuid === selectedUuid
              const pending = incidence.pendingActions || 0
              return (
                <ButtonBase
                  role='row'
                  key={incidence.uuid}
                  aria-selected={isSelected}
                  data-testid={'incidence-row-' + incidence.code}
                  onClick={() => onSelect(incidence.uuid)}
                  sx={{
                    display: 'grid',
                    width: '100%',
                    gridTemplateColumns: INCIDENCE_GRID,
                    gap: 2,
                    alignItems: 'center',
                    textAlign: 'left',
                    px: 2,
                    minHeight: 54,
                    borderBottom: 1,
                    borderColor: 'divider',
                    bgcolor: isSelected ? 'action.selected' : 'transparent',
                    '&:last-of-type': { borderBottom: 0 }
                  }}
                >
                  <Typography role='cell' sx={{ fontFamily: 'monospace' }}>{incidence.code}</Typography>
                  <Typography role='cell' noWrap title={incidence.title}>{incidence.title}</Typography>
                  <Box role='cell'><Pill label={t_(priorityLabelKey(incidence.priority))} tone={PRIORITY_TONE[incidence.priority] || 'default'} /></Box>
                  <Box role='cell'><SystemValueChip look='overline' domain='QualityIncidenceStatusEnum' value={incidence.status} /></Box>
                  <Typography role='cell' sx={{ fontWeight: 800, color: pending > 0 ? 'error.light' : 'text.primary' }} data-testid={'incidence-pending-' + incidence.code}>
                    {pending > 0 ? t_('{{count}} actions pending', { count: pending }) : t_('No actions pending')}
                  </Typography>
                </ButtonBase>
              )
            })}
          </Box>
        </DetailSection>

        {selected
          ? (
            <DetailSection title={t_('Actions of {{code}}', { code: selected.code })} testId='incidence-actions'>
              <Box role='table' aria-label={t_('Actions of {{code}}', { code: selected.code })}>
                <Box role='row' sx={{ display: 'grid', gridTemplateColumns: ACTION_GRID, gap: 2, px: 2, py: 1, borderBottom: 1, borderColor: 'divider' }}>
                  <HeaderCell>{t_('Action')}</HeaderCell>
                  <HeaderCell>{t_('Responsible')}</HeaderCell>
                  <HeaderCell>{t_('Date')}</HeaderCell>
                  <HeaderCell>{t_('Status')}</HeaderCell>
                </Box>
                {(selected.actions || []).map((action) => (
                  <Box
                    role='row'
                    key={action.uuid}
                    data-testid={'incidence-action-' + action.uuid}
                    sx={{ display: 'grid', gridTemplateColumns: ACTION_GRID, gap: 2, alignItems: 'center', px: 2, minHeight: 52, borderBottom: 1, borderColor: 'divider', '&:last-of-type': { borderBottom: 0 } }}
                  >
                    <Typography role='cell'>{action.description}</Typography>
                    {/* D21: el GRUPO responsable; nunca una persona. */}
                    <Typography role='cell' color='text.secondary'>{action.responsibleGroup || EMPTY_VALUE}</Typography>
                    <Typography role='cell' color='text.secondary'>{actionDate(action)}</Typography>
                    <Box role='cell'><SystemValueChip look='overline' domain='QualityIncidenceActionStatusEnum' value={action.status} tone={ACTION_TONE[action.status] || 'default'} /></Box>
                  </Box>
                ))}
              </Box>
            </DetailSection>
            )
          : null}
      </Stack>
    </BlockState>
  )
}

IncidencesTab.propTypes = {
  block: PropTypes.object,
  selectedUuid: PropTypes.string,
  onSelect: PropTypes.func,
  onRetry: PropTypes.func
}

export default React.memo(IncidencesTab)
